import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { brandColorPattern, type ReferrerPartner } from '@brimo/shared';
import {
  Banner,
  Button,
  Dialog,
  Select,
  TextField,
  Textarea,
  Toggle,
  useToast,
} from '../../../../ds/components';
import { ApiError, errorMessage } from '../../../../lib/api';
import { useBrand } from '../../../onboarding/admin/api';
import { ColorSwatches } from '../../../onboarding/admin/BrandParts';
import { useUserDirectory } from '../../../users/directory';
import {
  useCreateReferrerPartner,
  type CreateReferrerPartnerInput,
  useRemoveReferrerLogo,
  useUpdateReferrerPartner,
  useUploadReferrerLogo,
} from './api';
import styles from './ReferrerPartnersPage.module.css';

/** Roles the API accepts as a partner's default advisor (admin, sales lead, advisor, back office). */
const CLOSER_ROLES = ['admin', 'sales_lead', 'advisor', 'back_office'] as const;
const LOGO_TYPES = 'image/png,image/jpeg,image/webp,image/svg+xml,.png,.jpg,.jpeg,.webp,.svg';
const LOGO_MAX_BYTES = 2 * 1024 * 1024;
const LOGO_RULE = 'Nur PNG, JPG, WebP oder SVG bis 2 MB.';

export interface ReferrerPartnerDialogProps {
  open: boolean;
  /** The partner to edit; null creates a new one. */
  partner: ReferrerPartner | null;
  onClose: () => void;
  /** Called with the created partner (the page then offers its link). */
  onCreated?: ((partner: ReferrerPartner) => void) | undefined;
}

interface Values {
  name: string;
  contactName: string;
  contactEmail: string;
  defaultCloserId: string;
  brandColor: string;
  note: string;
  active: boolean;
}

function valuesOf(partner: ReferrerPartner | null): Values {
  return {
    name: partner?.name ?? '',
    contactName: partner?.contactName ?? '',
    contactEmail: partner?.contactEmail ?? '',
    defaultCloserId: partner?.defaultCloserId ?? '',
    brandColor: partner?.brandColor ?? '',
    note: partner?.note ?? '',
    active: partner?.active ?? true,
  };
}

const textOrNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/**
 * „Tippgeber-Partner anlegen\" / „Tippgeber-Partner bearbeiten\": Name*, Ansprechpartner, E-Mail, Standard-Berater
 * (active users with closer capability), Markenfarbe (the swatches and hex field of the brand page), Logo, Hinweis
 * and — when editing — „Aktiv\". A logo picked while creating is uploaded right after the partner exists.
 */
export function ReferrerPartnerDialog({ open, partner, onClose, onCreated }: ReferrerPartnerDialogProps) {
  const { toast } = useToast();
  const editing = partner !== null;
  const brand = useBrand();
  const directory = useUserDirectory({ roles: CLOSER_ROLES }, open);
  const create = useCreateReferrerPartner();
  const update = useUpdateReferrerPartner();
  const uploadLogo = useUploadReferrerLogo();
  const removeLogo = useRemoveReferrerLogo();
  const [values, setValues] = useState<Values>(() => valuesOf(partner));
  const [fields, setFields] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  /** Cache key of the authenticated preview: a new upload keeps the URL, the scan answers 409 until it is clean. */
  const [logoVersion, setLogoVersion] = useState(0);
  const [previewAttempts, setPreviewAttempts] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setValues(valuesOf(partner));
    setFields({});
    setBanner(null);
    setLogoFile(null);
    setLogoError(null);
    setPreviewFailed(false);
    setPreviewAttempts(0);
  }, [open, partner]);

  // While the uploaded logo is still being scanned, retry the preview a few times.
  useEffect(() => {
    if (!open || !previewFailed || !partner?.hasLogo || previewAttempts >= 12) return;
    const timer = window.setTimeout(() => {
      setPreviewAttempts((count) => count + 1);
      setLogoVersion((version) => version + 1);
      setPreviewFailed(false);
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [open, previewFailed, partner?.hasLogo, previewAttempts]);

  const localPreview = useMemo(() => (logoFile ? URL.createObjectURL(logoFile) : null), [logoFile]);
  useEffect(() => () => (localPreview ? URL.revokeObjectURL(localPreview) : undefined), [localPreview]);

  const closerOptions = useMemo(() => {
    const options = [
      { value: '', label: 'Erste Admin-Person der Organisation' },
      ...(directory.data ?? []).map((user) => ({ value: user.id, label: user.name })),
    ];
    // a former default advisor who is no longer active stays visible as the current value
    if (
      partner?.defaultCloserId &&
      directory.data &&
      !directory.data.some((user) => user.id === partner.defaultCloserId)
    ) {
      options.push({
        value: partner.defaultCloserId,
        label: `${partner.defaultCloserName ?? 'Unbekannt'} (nicht mehr aktiv)`,
      });
    }
    return options;
  }, [directory.data, partner]);

  function set<K extends keyof Values>(key: K, value: Values[K]) {
    setValues((current) => ({ ...current, [key]: value }));
    setFields((current) => {
      if (!(key in current)) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  function pickLogo(file: File) {
    setLogoError(null);
    const typeOk =
      /^image\/(png|jpeg|webp|svg\+xml)$/.test(file.type) || /\.(png|jpe?g|webp|svg)$/i.test(file.name);
    if (!typeOk || file.size > LOGO_MAX_BYTES) {
      setLogoError(LOGO_RULE);
      return;
    }
    if (editing) {
      uploadLogo.mutate(
        { id: partner.id, file },
        {
          onSuccess: () => {
            setPreviewFailed(false);
            setPreviewAttempts(0);
            setLogoVersion((version) => version + 1);
            toast({ message: 'Logo gespeichert. Es erscheint nach der Prüfung im Formular.' });
          },
          onError: (error) => setLogoError(errorMessage(error)),
        },
      );
    } else {
      setLogoFile(file);
    }
  }

  function dropLogo() {
    setLogoError(null);
    if (editing) {
      removeLogo.mutate(partner.id, { onError: (error) => setLogoError(errorMessage(error)) });
    } else {
      setLogoFile(null);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBanner(null);
    const color = values.brandColor.trim().toUpperCase();
    const local: Record<string, string> = {};
    if (values.name.trim().length < 2)
      local['name'] = 'Bitte geben Sie einen Namen mit 2 bis 120 Zeichen an.';
    if (color !== '' && !brandColorPattern.test(color))
      local['brandColor'] = 'Bitte eine Farbe im Format #RRGGBB angeben.';
    if (Object.keys(local).length > 0) {
      setFields(local);
      return;
    }
    const body: CreateReferrerPartnerInput = {
      name: values.name.trim(),
      contactName: textOrNull(values.contactName),
      contactEmail: textOrNull(values.contactEmail),
      brandColor: color === '' ? null : color,
      defaultCloserId: values.defaultCloserId === '' ? null : values.defaultCloserId,
      note: textOrNull(values.note),
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: partner.id, body: { ...body, active: values.active } });
        toast({ message: `${body.name} gespeichert.` });
        onClose();
        return;
      }
      const created = await create.mutateAsync(body);
      if (logoFile) {
        try {
          await uploadLogo.mutateAsync({ id: created.id, file: logoFile });
        } catch (error) {
          toast({
            message: `${created.name} angelegt. Das Logo konnte nicht gespeichert werden: ${errorMessage(error)}`,
          });
          onCreated?.(created);
          onClose();
          return;
        }
      }
      toast({ message: `${created.name} angelegt.` });
      onCreated?.(created);
      onClose();
    } catch (error) {
      if (error instanceof ApiError && Object.keys(error.fields).length > 0) setFields(error.fields);
      else setBanner(errorMessage(error));
    }
  }

  const saving = create.isPending || update.isPending || (uploadLogo.isPending && !editing);
  const color = values.brandColor.trim().toUpperCase();
  const hasServerLogo = editing && partner.hasLogo && partner.logoUrl !== null;
  const preview = localPreview ?? (hasServerLogo ? `${partner.logoUrl}?v=${logoVersion}` : null);
  const logoBusy = uploadLogo.isPending || removeLogo.isPending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Tippgeber"
      title={editing ? partner.name : 'Tippgeber-Partner anlegen'}
      subtitle={editing ? 'Tippgeber-Partner bearbeiten' : undefined}
      width={600}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" form="referrer-partner-form" loading={saving}>
            {editing ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <form
        id="referrer-partner-form"
        className={styles.form}
        onSubmit={(event) => void submit(event)}
        noValidate
      >
        <TextField
          label="Name"
          required
          value={values.name}
          maxLength={120}
          error={fields['name']}
          onChange={(event) => set('name', event.target.value)}
        />
        <div className={styles.formGrid}>
          <TextField
            label="Ansprechpartner Name"
            optional
            value={values.contactName}
            maxLength={120}
            error={fields['contactName']}
            onChange={(event) => set('contactName', event.target.value)}
          />
          <TextField
            label="E-Mail"
            type="email"
            optional
            value={values.contactEmail}
            maxLength={200}
            error={fields['contactEmail']}
            onChange={(event) => set('contactEmail', event.target.value)}
          />
        </div>
        <Select
          label="Standard-Berater"
          value={values.defaultCloserId}
          options={closerOptions}
          hint="Erhält den Deal und die Prüfaufgabe jeder Einreichung."
          error={fields['defaultCloserId']}
          onChange={(event) => set('defaultCloserId', event.target.value)}
        />

        <div className={styles.block}>
          <span className={styles.blockLabel} id="referrer-color-label">
            Markenfarbe
          </span>
          <div className={styles.colorRow} aria-labelledby="referrer-color-label">
            <ColorSwatches
              presets={brand.data?.colorPresets ?? []}
              value={color === '' ? null : color}
              withHex
              compact
              onPick={(value) => set('brandColor', value)}
            />
            {color !== '' ? (
              <button type="button" className={styles.linkButton} onClick={() => set('brandColor', '')}>
                Keine eigene Farbe
              </button>
            ) : null}
          </div>
          <span className={fields['brandColor'] ? styles.blockError : styles.blockHint}>
            {fields['brandColor'] ??
              'Färbt Schaltflächen und Hervorhebungen im Einreich-Formular. Ohne eigene Farbe gilt die Farbe der Organisation.'}
          </span>
        </div>

        <div className={styles.block}>
          <span className={styles.blockLabel}>Logo</span>
          <div className={styles.logoRow}>
            <div className={styles.logoPreview}>
              {preview && !previewFailed ? (
                <img
                  src={preview}
                  alt={`Logo ${values.name || 'Tippgeber-Partner'}`}
                  onError={() => setPreviewFailed(true)}
                />
              ) : (
                <span className={styles.logoEmpty}>
                  {hasServerLogo ? 'wird geprüft' : 'Noch nicht hinterlegt'}
                </span>
              )}
            </div>
            <div className={styles.logoActions}>
              <Button
                variant="secondary"
                size="small"
                loading={uploadLogo.isPending && editing}
                disabled={logoBusy}
                onClick={() => fileInput.current?.click()}
              >
                {preview ? 'Logo ersetzen' : 'Logo hochladen'}
              </Button>
              {preview ? (
                <Button variant="ghost" size="small" disabled={logoBusy} onClick={dropLogo}>
                  Logo entfernen
                </Button>
              ) : null}
              <input
                ref={fileInput}
                type="file"
                accept={LOGO_TYPES}
                className={styles.fileInput}
                tabIndex={-1}
                aria-hidden="true"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file) pickLogo(file);
                }}
              />
            </div>
          </div>
          <span
            className={logoError ? styles.blockError : styles.blockHint}
            role={logoError ? 'alert' : undefined}
          >
            {logoError ?? `${LOGO_RULE} Ohne eigenes Logo erscheint das Logo der Organisation.`}
          </span>
        </div>

        <Textarea
          label="Hinweis"
          optional
          value={values.note}
          rows={3}
          maxLength={2000}
          error={fields['note']}
          onChange={(event) => set('note', event.target.value)}
        />
        {editing ? (
          <div className={styles.toggleRow}>
            <Toggle
              size="large"
              label="Aktiv"
              checked={values.active}
              onChange={(value) => set('active', value)}
            />
            <span className={styles.blockHint}>
              Ein inaktiver Partner kann über seinen Link nichts mehr einreichen.
            </span>
          </div>
        ) : null}
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
      </form>
    </Dialog>
  );
}
